import { useEffect, useState } from 'react';

// T290b — overlays mounted OUTSIDE the page tree, so a page cannot see them.
//
// Escape is coordinated in this app by a prop: an overlay passes
// `escapeClosesOnTop={false}` while something sits above it. That works when
// both overlays are rendered by the same component — DashboardPage knows about
// its AI modals and its dismiss dialog and can gate the drawer on them.
//
// It does not work for the command palette, which App.jsx mounts once at the
// root, outside <Routes>. Both it and the drawer listen for Escape on `window`,
// and neither can stop the other: two listeners on the SAME target are not
// separated by stopPropagation, and stopImmediatePropagation would only work if
// the palette's listener happened to be registered first. So one keypress closed
// the palette AND the drawer behind it — the operator opened a job, pressed ⌘K
// to look something up, changed his mind, and lost his place in the queue.
//
// This is the missing channel: a globally-mounted overlay announces itself here,
// and a page gates its own overlays on it. Deliberately tiny — it is not an
// overlay manager, and the prop mechanism above stays the way same-tree overlays
// coordinate.
const _listeners = new Set();
const _open = new Set();

function notify() {
  const anyOpen = _open.size > 0;
  _listeners.forEach((fn) => fn(anyOpen));
}

export function openGlobalOverlay(id) {
  _open.add(id);
  notify();
}

export function closeGlobalOverlay(id) {
  _open.delete(id);
  notify();
}

export function isGlobalOverlayOpen() {
  return _open.size > 0;
}

export function subscribeGlobalOverlays(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// Test seam: a component that unmounts mid-test would otherwise leave its id
// behind and make the next test start with an overlay "open".
export function _resetGlobalOverlays() {
  _open.clear();
  notify();
}

// The hook a page uses to gate its own overlays. Subscribes rather than polling,
// so the gate flips in the same commit the palette opens in.
export function useGlobalOverlayOpen() {
  const [open, setOpen] = useState(isGlobalOverlayOpen);
  useEffect(() => {
    setOpen(isGlobalOverlayOpen());
    return subscribeGlobalOverlays(setOpen);
  }, []);
  return open;
}
